import { action, observable, makeObservable } from 'mobx';
import { Model, IModelAttributes, attribute, entity } from 'Models/Model';
import { isNotNullOrUndefined } from 'Util/TypeGuards';
import * as Models from 'Models/Entities';

export interface IParkingReservationEntityAttributes extends IModelAttributes {
	humanReadableId: string;
	status: string;
	totalPrice: number;
	note?: string;
	userId: string;
	user: Models.UserEntity | Models.IUserEntityAttributes;
	transactionId?: string;
	transaction?: Models.TransactionEntity | Models.ITransactionEntityAttributes;
	parkingLocationId: string;
	parkingLocation: Models.ParkingLocationEntity | Models.IParkingLocationEntityAttributes;
	vehicles: Models.ParkingReservationVehicleEntity[] | Models.IParkingReservationVehicleEntityAttributes[];
}

@entity('ParkingReservationEntity', 'Parking Reservation')
export default class ParkingReservationEntity extends Model implements IParkingReservationEntityAttributes {
	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	@observable
	@attribute<ParkingReservationEntity, string>()
	public humanReadableId: string;

	@observable
	@attribute<ParkingReservationEntity, string>()
	public status: string;

	@observable
	@attribute<ParkingReservationEntity, number>()
	public totalPrice: number;

	@observable
	@attribute<ParkingReservationEntity, string>()
	public note?: string;

	@observable
	@attribute()
	public userId: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public user: Models.UserEntity;

	@observable
	@attribute()
	public transactionId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public transaction?: Models.TransactionEntity;

	@observable
	@attribute()
	public parkingLocationId: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public parkingLocation: Models.ParkingLocationEntity;

	@observable
	@attribute({ isReference: true, manyReference: true })
	public vehicles: Models.ParkingReservationVehicleEntity[] = [];

	constructor(attributes?: Partial<IParkingReservationEntityAttributes>) {
		super(attributes);

		makeObservable(this);

		this.assignAttributes(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	public assignAttributes(attributes?: Partial<IParkingReservationEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (isNotNullOrUndefined(attributes.humanReadableId)) {
				this.humanReadableId = attributes.humanReadableId;
			}
			if (isNotNullOrUndefined(attributes.status)) {
				this.status = attributes.status;
			}
			if (isNotNullOrUndefined(attributes.totalPrice)) {
				this.totalPrice = attributes.totalPrice;
			}
			if (isNotNullOrUndefined(attributes.note)) {
				this.note = attributes.note;
			}
			if (isNotNullOrUndefined(attributes.userId)) {
				this.userId = attributes.userId;
			}
			if (isNotNullOrUndefined(attributes.user)) {
				if (attributes.user === null) {
					this.user = attributes.user;
				} else if (attributes.user instanceof Models.UserEntity) {
					this.user = attributes.user;
					this.userId = attributes.user.id;
				} else {
					this.user = new Models.UserEntity(attributes.user);
					this.userId = this.user.id;
				}
			}
			if (isNotNullOrUndefined(attributes.transactionId)) {
				this.transactionId = attributes.transactionId;
			}
			if (isNotNullOrUndefined(attributes.transaction)) {
				if (attributes.transaction === null) {
					this.transaction = attributes.transaction;
				} else if (attributes.transaction instanceof Models.TransactionEntity) {
					this.transaction = attributes.transaction;
					this.transactionId = attributes.transaction.id;
				} else {
					this.transaction = new Models.TransactionEntity(attributes.transaction);
					this.transactionId = this.transaction.id;
				}
			}
			if (isNotNullOrUndefined(attributes.parkingLocationId)) {
				this.parkingLocationId = attributes.parkingLocationId;
			}
			if (isNotNullOrUndefined(attributes.parkingLocation)) {
				if (attributes.parkingLocation === null) {
					this.parkingLocation = attributes.parkingLocation;
				} else if (attributes.parkingLocation instanceof Models.ParkingLocationEntity) {
					this.parkingLocation = attributes.parkingLocation;
					this.parkingLocationId = attributes.parkingLocation.id;
				} else {
					this.parkingLocation = new Models.ParkingLocationEntity(attributes.parkingLocation);
					this.parkingLocationId = this.parkingLocation.id;
				}
			}
			if (isNotNullOrUndefined(attributes.vehicles)) {
				this.vehicles = attributes.vehicles.map(v => {
					if (v instanceof Models.ParkingReservationVehicleEntity) {
						return v;
					}
					return new Models.ParkingReservationVehicleEntity(v);
				});
			}
		}
	}
}
